/**
 * Product catalog — single source for UI + SEO structured data.
 * Store links with verified: false must never render as badges.
 * Artwork lives in productImages.js (Vite assets; not imported here so
 * Node can load this module from vite.config).
 */

/**
 * @typedef {'games'|'extensions'|'apps'} ProductCategory
 * @typedef {'live'|'coming_soon'} ProductStatus
 * @typedef {'app_store'|'google_play'|'amazon'|'chrome_web_store'|'web'} StorePlatform
 *
 * @typedef {object} StoreLink
 * @property {StorePlatform} platform
 * @property {string} url
 * @property {boolean} [enabled]
 * @property {boolean} [verified]
 *
 * @typedef {object} Product
 * @property {string} id
 * @property {string} title
 * @property {ProductCategory} category
 * @property {string} [description]
 * @property {string} [imagePublic] optional public path for SEO (under /images/)
 * @property {StoreLink[]} storeLinks
 * @property {ProductStatus} status
 * @property {string} primaryUrl
 * @property {number} [order]
 * @property {string} [applicationCategory]
 * @property {string} [operatingSystem]
 */

/** @type {Product[]} */
export const products = [
  {
    id: 'daily-quiz',
    title: 'Daily Quiz',
    category: 'games',
    description:
      'Fresh trivia every day — race the timer, earn XP and badges, and climb daily, weekly, and monthly leaderboards.',
    imagePublic: '/images/daily-quiz.png',
    status: 'live',
    primaryUrl: 'https://quiz.balconygames.com',
    applicationCategory: 'Game',
    operatingSystem: 'Web',
    order: 1,
    storeLinks: [
      {
        platform: 'web',
        url: 'https://quiz.balconygames.com',
        verified: true,
      },
    ],
  },
  {
    id: 'crystal-blast-3d',
    title: 'Crystal Blast 3D',
    category: 'games',
    description:
      'Ball Blast–style 3D crystal shooter — ink-and-parchment arcade action in the browser.',
    imagePublic: '/images/crystal-blast-3d.png',
    status: 'live',
    primaryUrl: 'https://gun.balconygames.com',
    applicationCategory: 'Game',
    operatingSystem: 'Web',
    order: 2,
    storeLinks: [
      {
        platform: 'web',
        url: 'https://gun.balconygames.com',
        verified: true,
      },
    ],
  },
  {
    id: 'ink-surge',
    title: 'Ink Surge',
    category: 'games',
    description:
      'Survive the living page — hand-drawn ink survival on parchment in your browser.',
    imagePublic: '/images/ink-surge.png',
    status: 'live',
    primaryUrl: 'https://ink.balconygames.com',
    applicationCategory: 'Game',
    operatingSystem: 'Web',
    order: 3,
    storeLinks: [
      {
        platform: 'web',
        url: 'https://ink.balconygames.com',
        verified: true,
      },
    ],
  },
  {
    id: 'magekeep-siege',
    title: 'Magekeep Siege',
    category: 'games',
    description:
      'Idle tower defense — defend the keep, upgrade your mage, and survive the waves. Also listed on Google Play as Mage Tower.',
    imagePublic: '/images/magekeep-siege.png',
    status: 'live',
    primaryUrl: 'https://mage.balconygames.com',
    applicationCategory: 'Game',
    operatingSystem: 'Web, Android',
    order: 4,
    storeLinks: [
      {
        platform: 'web',
        url: 'https://mage.balconygames.com',
        verified: true,
      },
      {
        platform: 'google_play',
        url: 'https://play.google.com/store/apps/details?id=com.balconygames.towermage',
        verified: true,
      },
    ],
  },
  {
    id: 'shoot',
    title: 'Scrapflare',
    category: 'games',
    description:
      'Twin-stick exploration and bullet-hell combat — dash through biomes, unlock abilities, and conquer a huge paper-art world.',
    imagePublic: '/images/shoot.png',
    status: 'live',
    primaryUrl: 'https://shoot.balconygames.com',
    applicationCategory: 'Game',
    operatingSystem: 'Web',
    order: 5,
    storeLinks: [
      {
        platform: 'web',
        url: 'https://shoot.balconygames.com',
        verified: true,
      },
    ],
  },
  {
    id: 'crystal-blast',
    title: 'Crystal Blast',
    category: 'games',
    description: 'Match and blast crystals in a fast arcade puzzler for mobile.',
    status: 'live',
    primaryUrl: 'https://crystalblast.page.link/install',
    applicationCategory: 'Game',
    operatingSystem: 'iOS, Android',
    order: 6,
    storeLinks: [
      {
        platform: 'app_store',
        url: 'https://apps.apple.com/us/app/crystal-blast/id1451868968',
        verified: true,
      },
      {
        platform: 'google_play',
        url: 'https://play.google.com/store/apps/details?id=com.balconygames.CrystalBlast',
        verified: true,
      },
      {
        platform: 'amazon',
        url: 'https://www.amazon.com/-/es/dp/B07W1JL921',
        verified: true,
      },
    ],
  },
  {
    id: 'sweety-jumper',
    title: 'Sweety Jumper',
    category: 'games',
    description: 'Jump and climb in this sweet endless platformer.',
    status: 'live',
    primaryUrl: 'https://sweetyjumper.page.link/install',
    applicationCategory: 'Game',
    operatingSystem: 'iOS, Android',
    order: 7,
    storeLinks: [
      // Known-bad App Store ID omitted (Don’t Fall) — do not re-add without verification
      {
        platform: 'google_play',
        url: 'https://play.google.com/store/apps/details?id=com.balconygames.sweetyjumper',
        verified: true,
      },
    ],
  },
  {
    id: 'snake',
    title: 'Snake',
    category: 'games',
    description: 'Classic Snake reimagined for modern mobile play.',
    status: 'live',
    primaryUrl: 'https://snakeio.page.link/install',
    applicationCategory: 'Game',
    operatingSystem: 'iOS, Android',
    order: 8,
    storeLinks: [
      {
        platform: 'app_store',
        url: 'https://apps.apple.com/by/app/snake-by-balcony-games/id1460842595',
        verified: true,
      },
      {
        platform: 'google_play',
        url: 'https://play.google.com/store/apps/details?id=com.balconygames.snake',
        verified: true,
      },
    ],
  },
  {
    id: 'dont-fall',
    title: "Don't Fall",
    category: 'games',
    description: 'Stay on the platforms and don’t fall in this arcade challenge.',
    status: 'live',
    primaryUrl: 'https://dontfall.page.link/install',
    applicationCategory: 'Game',
    operatingSystem: 'iOS, Android',
    order: 9,
    storeLinks: [
      {
        platform: 'app_store',
        url: 'https://apps.apple.com/es/app/dont-fall-by-balcony-games/id1460842492',
        verified: true,
      },
      {
        platform: 'google_play',
        url: 'https://play.google.com/store/apps/details?id=com.balconygames.dontfall',
        verified: true,
      },
    ],
  },
  {
    id: 'bricks-breaker',
    title: 'Bricks Breaker',
    category: 'games',
    description: 'Break the bricks with satisfying ball physics and power-ups.',
    status: 'live',
    primaryUrl: 'https://apps.apple.com/us/app/bricks-breaker-by-balconygames/id1638905755',
    applicationCategory: 'Game',
    operatingSystem: 'iOS',
    order: 10,
    storeLinks: [
      {
        platform: 'app_store',
        url: 'https://apps.apple.com/us/app/bricks-breaker-by-balconygames/id1638905755',
        verified: true,
      },
    ],
  },
  {
    id: 'dice-blast',
    title: 'Dice Blast',
    category: 'games',
    description: 'Dice-powered arcade action — coming soon.',
    status: 'coming_soon',
    primaryUrl: 'https://dice-blast-web.web.app/',
    applicationCategory: 'Game',
    operatingSystem: 'Web',
    order: 11,
    storeLinks: [
      {
        platform: 'web',
        url: 'https://dice-blast-web.web.app/',
        verified: true,
      },
    ],
  },
  {
    id: 'dots-game',
    title: 'Dots Game',
    category: 'extensions',
    description: 'A quick dots puzzle game as a Chrome extension.',
    status: 'live',
    primaryUrl:
      'https://chromewebstore.google.com/detail/dots-game/pohbllgenaajfpjkncldjkifdelobdfn',
    applicationCategory: 'BrowserApplication',
    operatingSystem: 'Chrome',
    order: 1,
    storeLinks: [
      {
        platform: 'chrome_web_store',
        url: 'https://chromewebstore.google.com/detail/dots-game/pohbllgenaajfpjkncldjkifdelobdfn',
        verified: true,
      },
    ],
  },
  {
    id: 'learn-basque',
    title: 'Learn Basque',
    category: 'extensions',
    description: 'Practice Basque vocabulary from your Chrome new tab.',
    status: 'live',
    primaryUrl:
      'https://chromewebstore.google.com/detail/learn-basque/goplofbdpdkeopimioaecokgpfcaooom',
    applicationCategory: 'EducationalApplication',
    operatingSystem: 'Chrome',
    order: 2,
    storeLinks: [
      {
        platform: 'chrome_web_store',
        url: 'https://chromewebstore.google.com/detail/learn-basque/goplofbdpdkeopimioaecokgpfcaooom',
        verified: true,
      },
    ],
  },
  {
    id: 'learn-french',
    title: 'Learn French',
    category: 'extensions',
    description: 'Learn French words and phrases with a Chrome extension.',
    status: 'live',
    primaryUrl:
      'https://chromewebstore.google.com/detail/learn-french/jajgjaibgcfdidjidojidenpfcfjeaoj',
    applicationCategory: 'EducationalApplication',
    operatingSystem: 'Chrome',
    order: 4,
    storeLinks: [
      {
        platform: 'chrome_web_store',
        url: 'https://chromewebstore.google.com/detail/learn-french/jajgjaibgcfdidjidojidenpfcfjeaoj',
        verified: true,
      },
    ],
  },
  {
    id: 'learn-spanish',
    title: 'Learn Spanish',
    category: 'extensions',
    description: 'Learn Spanish vocabulary with a Chrome extension.',
    status: 'live',
    primaryUrl:
      'https://chromewebstore.google.com/detail/learn-spanish/onnolcnjhjbmcgfppkmamfkadklopbkj',
    applicationCategory: 'EducationalApplication',
    operatingSystem: 'Chrome',
    order: 5,
    storeLinks: [
      {
        platform: 'chrome_web_store',
        url: 'https://chromewebstore.google.com/detail/learn-spanish/onnolcnjhjbmcgfppkmamfkadklopbkj',
        verified: true,
      },
    ],
  },
  {
    id: 'pushup-challenges',
    title: 'PushUP Challenges',
    category: 'apps',
    description: 'Track push-up challenges and build a fitness habit.',
    status: 'live',
    primaryUrl: 'https://pushup.bitscorp.co/',
    applicationCategory: 'HealthApplication',
    operatingSystem: 'iOS, Android',
    order: 1,
    storeLinks: [
      {
        platform: 'app_store',
        url: 'https://apps.apple.com/us/app/pushup-challenges/id6450053262',
        verified: true,
      },
      {
        platform: 'google_play',
        url: 'https://play.google.com/store/apps/details?id=co.bitscorp.pushup',
        verified: true,
      },
      {
        platform: 'web',
        url: 'https://pushup.bitscorp.co/',
        verified: true,
      },
    ],
  },
  {
    id: 'palm-reader-ai',
    title: 'Palm Reader AI',
    category: 'apps',
    description: 'Explore palm reading insights powered by AI.',
    status: 'live',
    primaryUrl: 'https://palm.bitscorp.co/',
    applicationCategory: 'LifestyleApplication',
    operatingSystem: 'Android, Web',
    order: 2,
    storeLinks: [
      {
        platform: 'google_play',
        url: 'https://play.google.com/store/apps/details?id=co.bitscorp.palm',
        verified: true,
      },
      {
        platform: 'web',
        url: 'https://palm.bitscorp.co/',
        verified: true,
      },
    ],
  },
  {
    id: 'catalan-verbs',
    title: 'Catalan Verbs',
    category: 'apps',
    description: 'Practice Catalan verb conjugations on the go.',
    status: 'live',
    primaryUrl: 'https://apps.apple.com/es/app/catalan-verbs/id6738144158',
    applicationCategory: 'EducationalApplication',
    operatingSystem: 'iOS, Android',
    order: 3,
    storeLinks: [
      {
        platform: 'app_store',
        url: 'https://apps.apple.com/es/app/catalan-verbs/id6738144158',
        verified: true,
      },
      {
        platform: 'google_play',
        url: 'https://play.google.com/store/apps/details?id=co.bitscorp.cat',
        verified: true,
      },
    ],
  },
  {
    id: 'images-to-documents',
    title: 'Images to Documents',
    category: 'apps',
    description: 'Turn photos into tidy documents on iOS.',
    status: 'live',
    primaryUrl:
      'https://apps.apple.com/es/app/images-to-documents/id6749373309?l=en-GB',
    applicationCategory: 'UtilitiesApplication',
    operatingSystem: 'iOS',
    order: 4,
    storeLinks: [
      {
        platform: 'app_store',
        url: 'https://apps.apple.com/es/app/images-to-documents/id6749373309?l=en-GB',
        verified: true,
      },
    ],
  },
  {
    id: 'tarot',
    title: 'Tarot Reader AI',
    category: 'apps',
    description: 'Draw tarot cards and explore daily readings with AI-assisted insights.',
    status: 'live',
    primaryUrl: 'https://play.google.com/store/apps/details?id=co.bitscorp.tarot',
    applicationCategory: 'LifestyleApplication',
    operatingSystem: 'Android',
    order: 5,
    storeLinks: [
      {
        platform: 'google_play',
        url: 'https://play.google.com/store/apps/details?id=co.bitscorp.tarot',
        verified: true,
      },
    ],
  },
  {
    id: 'italian-vocabulary',
    title: 'Italian Words | Parole',
    category: 'apps',
    description: 'Learn Italian words and phrases with a focused vocabulary trainer.',
    imagePublic: '/images/italian-vocabulary.png',
    status: 'live',
    primaryUrl: 'https://apps.apple.com/us/app/italian-words-parole/id6761471847',
    applicationCategory: 'EducationalApplication',
    operatingSystem: 'iOS, Android',
    order: 6,
    storeLinks: [
      {
        platform: 'app_store',
        url: 'https://apps.apple.com/us/app/italian-words-parole/id6761471847',
        verified: true,
      },
      {
        platform: 'google_play',
        url: 'https://play.google.com/store/apps/details?id=co.bitscorp.italianwords',
        verified: true,
      },
    ],
  },
  {
    id: 'portuguese-vocabulary',
    title: 'Portuguese Words | Palavras',
    category: 'apps',
    description: 'Build Portuguese vocabulary with short daily practice sessions.',
    imagePublic: '/images/portuguese-vocabulary.png',
    status: 'live',
    primaryUrl: 'https://apps.apple.com/us/app/portuguese-words-palavras/id6761484992',
    applicationCategory: 'EducationalApplication',
    operatingSystem: 'iOS, Android',
    order: 7,
    storeLinks: [
      {
        platform: 'app_store',
        url: 'https://apps.apple.com/us/app/portuguese-words-palavras/id6761484992',
        verified: true,
      },
      {
        platform: 'google_play',
        url: 'https://play.google.com/store/apps/details?id=co.bitscorp.portuguesewords',
        verified: true,
      },
    ],
  },
  {
    id: 'french-words',
    title: 'French Words | Mots',
    category: 'apps',
    description: 'Practice French vocabulary (mots) with a simple mobile word trainer.',
    imagePublic: '/images/french-words.png',
    status: 'live',
    primaryUrl: 'https://apps.apple.com/us/app/french-words-mots/id6760758007',
    applicationCategory: 'EducationalApplication',
    operatingSystem: 'iOS, Android',
    order: 8,
    storeLinks: [
      {
        platform: 'app_store',
        url: 'https://apps.apple.com/us/app/french-words-mots/id6760758007',
        verified: true,
      },
      {
        platform: 'google_play',
        url: 'https://play.google.com/store/apps/details?id=co.bitscorp.frenchwords',
        verified: true,
      },
    ],
  },
  {
    id: 'spanish-vocabulary',
    title: 'Spanish Words | Palabras',
    category: 'apps',
    description: 'Learn Spanish words on the go with a focused vocabulary app.',
    imagePublic: '/images/spanish-vocabulary.png',
    status: 'live',
    primaryUrl: 'https://apps.apple.com/us/app/spanish-words-palabras/id6760658599',
    applicationCategory: 'EducationalApplication',
    operatingSystem: 'iOS, Android',
    order: 9,
    storeLinks: [
      {
        platform: 'app_store',
        url: 'https://apps.apple.com/us/app/spanish-words-palabras/id6760658599',
        verified: true,
      },
      {
        platform: 'google_play',
        url: 'https://play.google.com/store/apps/details?id=co.bitscorp.spanishwords',
        verified: true,
      },
    ],
  },
  {
    id: 'basque-vocabulary',
    title: 'Basque Vocabulary',
    category: 'apps',
    description: 'Practice Basque vocabulary with a dedicated mobile learning app.',
    imagePublic: '/images/basque-vocabulary.png',
    status: 'live',
    primaryUrl: 'https://apps.apple.com/us/app/basque-vocabulary/id6760285058',
    applicationCategory: 'EducationalApplication',
    operatingSystem: 'iOS, Android',
    order: 10,
    storeLinks: [
      {
        platform: 'app_store',
        url: 'https://apps.apple.com/us/app/basque-vocabulary/id6760285058',
        verified: true,
      },
      {
        platform: 'google_play',
        url: 'https://play.google.com/store/apps/details?id=co.bitscorp.basquewords',
        verified: true,
      },
    ],
  },
  {
    id: 'catalan-words',
    title: 'Catalan Words | Mots',
    category: 'apps',
    description: 'Build Catalan vocabulary with a mobile word trainer (separate from Catalan Verbs).',
    imagePublic: '/images/catalan-words.png',
    status: 'live',
    primaryUrl: 'https://apps.apple.com/us/app/catalan-words-mots/id6758340877',
    applicationCategory: 'EducationalApplication',
    operatingSystem: 'iOS, Android',
    order: 11,
    storeLinks: [
      {
        platform: 'app_store',
        url: 'https://apps.apple.com/us/app/catalan-words-mots/id6758340877',
        verified: true,
      },
      {
        platform: 'google_play',
        url: 'https://play.google.com/store/apps/details?id=co.bitscorp.catalanwords',
        verified: true,
      },
    ],
  },
  {
    id: 'italian-verbs',
    title: 'Italian Verbs',
    category: 'apps',
    description: 'Practice Italian verb conjugations with a dedicated mobile trainer.',
    imagePublic: '/images/italian-verbs.png',
    status: 'live',
    primaryUrl: 'https://apps.apple.com/us/app/italian-verbs-by-balconygames/id6740482830',
    applicationCategory: 'EducationalApplication',
    operatingSystem: 'iOS, Android',
    order: 12,
    storeLinks: [
      {
        platform: 'app_store',
        url: 'https://apps.apple.com/us/app/italian-verbs-by-balconygames/id6740482830',
        verified: true,
      },
      {
        platform: 'google_play',
        url: 'https://play.google.com/store/apps/details?id=co.bitscorp.it',
        verified: true,
      },
    ],
  },
  {
    id: 'german-verbs',
    title: 'German Verbs',
    category: 'apps',
    description: 'Master German verb conjugations with focused practice on mobile.',
    imagePublic: '/images/german-verbs.png',
    status: 'live',
    primaryUrl: 'https://apps.apple.com/us/app/german-verbs-by-balconygames/id6740482885',
    applicationCategory: 'EducationalApplication',
    operatingSystem: 'iOS, Android',
    order: 13,
    storeLinks: [
      {
        platform: 'app_store',
        url: 'https://apps.apple.com/us/app/german-verbs-by-balconygames/id6740482885',
        verified: true,
      },
      {
        platform: 'google_play',
        url: 'https://play.google.com/store/apps/details?id=co.bitscorp.de',
        verified: true,
      },
    ],
  },
  {
    id: 'portuguese-verbs',
    title: 'Portuguese Verbs',
    category: 'apps',
    description: 'Practice Portuguese verb conjugations on the go.',
    imagePublic: '/images/portuguese-verbs.png',
    status: 'live',
    primaryUrl: 'https://apps.apple.com/us/app/portuguese-verbs-by-balcony/id6740201947',
    applicationCategory: 'EducationalApplication',
    operatingSystem: 'iOS, Android',
    order: 14,
    storeLinks: [
      {
        platform: 'app_store',
        url: 'https://apps.apple.com/us/app/portuguese-verbs-by-balcony/id6740201947',
        verified: true,
      },
      {
        platform: 'google_play',
        url: 'https://play.google.com/store/apps/details?id=co.bitscorp.pt',
        verified: true,
      },
    ],
  },
  {
    id: 'spanish-verbs',
    title: 'Spanish Verbs',
    category: 'apps',
    description: 'Learn Spanish verb conjugations with a simple mobile practice app.',
    imagePublic: '/images/spanish-verbs.png',
    status: 'live',
    primaryUrl: 'https://apps.apple.com/us/app/spanish-verbs-by-balconygames/id6740201682',
    applicationCategory: 'EducationalApplication',
    operatingSystem: 'iOS, Android',
    order: 15,
    storeLinks: [
      {
        platform: 'app_store',
        url: 'https://apps.apple.com/us/app/spanish-verbs-by-balconygames/id6740201682',
        verified: true,
      },
      {
        platform: 'google_play',
        url: 'https://play.google.com/store/apps/details?id=co.bitscorp.es',
        verified: true,
      },
    ],
  },
  {
    id: 'french-verbs',
    title: 'French Verbs',
    category: 'apps',
    description: 'Practice French verb conjugations with a dedicated mobile trainer.',
    imagePublic: '/images/french-verbs.png',
    status: 'live',
    primaryUrl: 'https://apps.apple.com/us/app/french-verbs-by-balconygames/id6740201728',
    applicationCategory: 'EducationalApplication',
    operatingSystem: 'iOS, Android',
    order: 16,
    storeLinks: [
      {
        platform: 'app_store',
        url: 'https://apps.apple.com/us/app/french-verbs-by-balconygames/id6740201728',
        verified: true,
      },
      {
        platform: 'google_play',
        url: 'https://play.google.com/store/apps/details?id=co.bitscorp.fr',
        verified: true,
      },
    ],
  },
  {
    id: 'collage-lab',
    title: 'Collage Lab',
    category: 'apps',
    description: 'Easy photo collage maker — templates, text, and polished layouts for your photos and videos.',
    imagePublic: '/images/collage-lab.png',
    status: 'live',
    primaryUrl: 'https://apps.apple.com/us/app/collage-lab/id6751143231',
    applicationCategory: 'PhotoApplication',
    operatingSystem: 'iOS',
    order: 17,
    storeLinks: [
      {
        platform: 'app_store',
        url: 'https://apps.apple.com/us/app/collage-lab/id6751143231',
        verified: true,
      },
    ],
  },
  {
    id: 'sleep-notes-ai',
    title: 'Sleep Notes AI',
    category: 'apps',
    description: 'Private sleep, dreams, and mood journal — understand your nights and improve sleep habits.',
    imagePublic: '/images/sleep-notes-ai.png',
    status: 'live',
    primaryUrl: 'https://apps.apple.com/us/app/sleep-notes-ai/id6762139822',
    applicationCategory: 'HealthApplication',
    operatingSystem: 'iOS',
    order: 18,
    storeLinks: [
      {
        platform: 'app_store',
        url: 'https://apps.apple.com/us/app/sleep-notes-ai/id6762139822',
        verified: true,
      },
    ],
  },
  {
    id: 'dual-video-shot',
    title: 'Dual Video Shot',
    category: 'apps',
    description: 'Record with both cameras at once and create split-view portrait and landscape videos.',
    imagePublic: '/images/dual-video-shot.png',
    status: 'live',
    primaryUrl: 'https://apps.apple.com/us/app/dual-video-shot/id6761911912',
    applicationCategory: 'PhotoApplication',
    operatingSystem: 'iOS',
    order: 19,
    storeLinks: [
      {
        platform: 'app_store',
        url: 'https://apps.apple.com/us/app/dual-video-shot/id6761911912',
        verified: true,
      },
    ],
  },
  {
    id: 'shield-scam-blocker',
    title: 'Shield: Scam Text Blocker AI',
    category: 'apps',
    description: 'Block scam, phishing, and fraudulent SMS instantly with AI-powered message protection.',
    imagePublic: '/images/shield-scam-blocker.png',
    status: 'live',
    primaryUrl: 'https://apps.apple.com/us/app/shield-scam-text-blocker-ai/id6757823397',
    applicationCategory: 'UtilitiesApplication',
    operatingSystem: 'iOS',
    order: 20,
    storeLinks: [
      {
        platform: 'app_store',
        url: 'https://apps.apple.com/us/app/shield-scam-text-blocker-ai/id6757823397',
        verified: true,
      },
    ],
  },
  {
    id: 'llm-api-costs-widget',
    title: 'LLM API Costs Widget',
    category: 'apps',
    description: 'Track OpenAI API usage and costs at a glance with a home-screen widget.',
    imagePublic: '/images/llm-api-costs-widget.png',
    status: 'live',
    primaryUrl: 'https://apps.apple.com/us/app/llm-api-costs-widget/id6743623133',
    applicationCategory: 'UtilitiesApplication',
    operatingSystem: 'iOS',
    order: 21,
    storeLinks: [
      {
        platform: 'app_store',
        url: 'https://apps.apple.com/us/app/llm-api-costs-widget/id6743623133',
        verified: true,
      },
    ],
  },
]

/**
 * @param {StoreLink[]} links
 * @returns {StoreLink[]}
 */
export function visibleStoreLinks(links) {
  return links.filter((l) => l.enabled !== false && l.verified !== false && l.url)
}

/**
 * @param {ProductCategory} category
 * @returns {Product[]}
 */
export function byCategory(category) {
  return products
    .filter((p) => p.category === category)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
}

/**
 * @param {string} id
 * @returns {Product|undefined}
 */
export function getProduct(id) {
  return products.find((p) => p.id === id)
}

/**
 * Crawlable catalog URL on balconygames.com (not the play/store URL).
 * @param {Product} product
 */
export function catalogPath(product) {
  return `/${product.category}/${product.id}`
}

export const SECTION_META = {
  games: {
    id: 'games',
    title: 'Our Games',
    description: 'Browser and mobile arcade, survival, and puzzle games.',
  },
  extensions: {
    id: 'extensions',
    title: 'Chrome Extensions',
    description: 'Browser games and language-learning tools for Chrome.',
  },
  apps: {
    id: 'apps',
    title: 'Our Apps',
    description: 'Utility, fitness, language, and lifestyle apps.',
  },
}
